import store from '@/store';
import ActionStatus from '@/services/Action/ActionStatus';

/**
 * @class ActionPermissions
 * Handles complex permission logic for various action operations (assign, no access, query, etc.)
 * Refactored from AssignPermission to support generic operations
 */
class ActionPermissions {
  /**
   * Determines if user is an admin based on permission set
   * Admin: Has both 'assign' and 'view all actions' permissions, but NOT 'view team actions'
   * 
   * @param {Object} vue - Vue component instance (for accessing $can and $perm)
   * @returns {boolean}
   */
  static isAdmin(vue) {
    // Individual permission checks
    const hasAssignPermission = vue.$can(
      vue.$perm.ACTIONS_ASSIGN,
      vue.$perm.PERMISSION_TYPE_ACTION
    );

    const hasViewAllActionsPermission = vue.$can(
      vue.$perm.ACTIONS_VIEW_ALL,
      vue.$perm.PERMISSION_TYPE_ACTION
    );

    const hasViewTeamActionsPermission = vue.$can(
      vue.$perm.ACTIONS_VIEW_TEAM_ACTIONS,
      vue.$perm.PERMISSION_TYPE_ACTION
    );

    const isAdmin = hasAssignPermission && hasViewAllActionsPermission && !hasViewTeamActionsPermission;

    return isAdmin;
  }

  /**
   * Checks if user has 'add client actions' permission
   * 
   * @param {Object} vue - Vue component instance (for accessing $can and $perm)
   * @returns {boolean}
   */
  static canAddClientActions(vue) {
    return vue.$can(
      vue.$perm.ACTIONS_ADD_CLIENT_ACTIONS,
      vue.$perm.PERMISSION_TYPE_ACTION
    );
  }

  /**
   * Checks if a team ID is within the user's scope (their team or sub-teams)
   * Gets team data from the assign options API rather than user.teams
   * 
   * @param {number} teamId - The team ID to check
   * @param {Array} assignOptions - Array of assign options from ClientActionAssign.getFormattedOptions()
   * @returns {boolean}
   */
  static isTeamInScope(teamId, assignOptions = []) {
    if (!teamId || !Array.isArray(assignOptions)) {
      return false;
    }

    // assignOptions structure: [{ team: "Teams", items: [...] }, { team: "Team Name", items: [...] }]
    return assignOptions.some(option => {
      if (!option.items || !Array.isArray(option.items)) {
        return false;
      }
      
      return option.items.some(item => {
        const itemTeamId = item.teamId || item.team;
        return itemTeamId === teamId;
      });
    });
  }

  /**
   * Checks if the current user is the creator of the action
   * 
   * @param {Object} actionData - The action data object
   * @returns {boolean}
   */
  static isActionCreatedByUser(actionData) {
    const authUser = store.getters.authUser;
    const currentUserId = authUser && authUser.userId;
    
    if (!currentUserId || !actionData) {
      return false;
    }

    return actionData.createdByUserId === currentUserId;
  }

  /**
   * Gets all team IDs from selected actions
   * 
   * @param {Array} selectedNodes - Array of selected grid nodes
   * @returns {Array} Array of unique team IDs
   */
  static getSelectedTeamIds(selectedNodes) {
    if (!Array.isArray(selectedNodes) || selectedNodes.length === 0) {
      return [];
    }

    const teamIds = selectedNodes
      .map(node => node.data && node.data.activeTeamId)
      .filter(teamId => teamId !== null && teamId !== undefined);

    return [...new Set(teamIds)];
  }

  /**
   * Gets all actions from selected nodes, separated by assigned and unassigned
   * 
   * @param {Array} selectedNodes - Array of selected grid nodes
   * @returns {Object} Object with assigned and unassigned actions
   */
  static categorizeSelectedActions(selectedNodes) {
    if (!Array.isArray(selectedNodes) || selectedNodes.length === 0) {
      return { assigned: [], unassigned: [] };
    }

    const assigned = [];
    const unassigned = [];

    selectedNodes.forEach(node => {
      if (node.data) {
        if (node.data.activeTeamId) {
          assigned.push(node.data);
        } else {
          unassigned.push(node.data);
        }
      }
    });

    return { assigned, unassigned };
  }

  /**
   * Checks if all selected actions have team IDs that are in the user's scope
   * 
   * @param {Array} selectedNodes - Array of selected grid nodes
   * @param {Array} assignOptions - Array of assign options from ClientActionAssign.getFormattedOptions()
   * @returns {boolean}
   */
  static areAllSelectedTeamsInScope(selectedNodes, assignOptions = []) {
    const teamIds = this.getSelectedTeamIds(selectedNodes);
    
    if (teamIds.length === 0) {
      return false;
    }

    return teamIds.every(teamId => this.isTeamInScope(teamId, assignOptions));
  }

  /**
   * Checks if all unassigned actions in selection were created by the current user
   * 
   * @param {Array} unassignedActions - Array of unassigned action data objects
   * @returns {boolean}
   */
  static areAllUnassignedActionsCreatedByUser(unassignedActions) {
    if (!Array.isArray(unassignedActions) || unassignedActions.length === 0) {
      return true; // No unassigned actions, so condition is satisfied
    }

    return unassignedActions.every(action => this.isActionCreatedByUser(action));
  }

  /**
   * Checks if all selected actions have valid statuses for the given operation
   * 
   * @param {Array} selectedNodes - Array of selected grid nodes
   * @param {Array} validStatuses - Array of valid status strings for the operation
   * @returns {boolean}
   */
  static areAllActionsInValidStatus(selectedNodes, validStatuses) {
    if (!Array.isArray(selectedNodes) || selectedNodes.length === 0) {
      return false;
    }

    if (!Array.isArray(validStatuses) || validStatuses.length === 0) {
      return false;
    }

    return selectedNodes.every(node => {
      return node.data && validStatuses.includes(node.data.status);
    });
  }

  /**
   * Generic method to check if user can perform any action operation
   * This is the core method that handles all permission logic
   * 
   * @param {Object} vue - Vue component instance
   * @param {string} permissionType - Permission constant (e.g., ACTIONS_ASSIGN, ACTIONS_REJECT)
   * @param {Array} actionsOrNodes - Array of action data objects or selected grid nodes
   * @param {Array} userTeams - Array of assign options from ClientActionAssign.getFormattedOptions()
   * @param {Array} validStatuses - Optional array of valid statuses for this operation
   * @returns {boolean}
   */
  static canPerformActionOperation(vue, permissionType, actionsOrNodes = [], userTeams = [], validStatuses = null) {
    // Check basic permissions first
    const hasPermission = vue.$can(
      permissionType, 
      vue.$perm.PERMISSION_TYPE_ACTION
    );

    if (!hasPermission) {
      return false;
    }

    if (!Array.isArray(actionsOrNodes) || actionsOrNodes.length === 0) {
      return true;
    }

    // If user is admin, they can perform most operations (except status-specific ones)
    if (this.isAdmin(vue)) {
      if (validStatuses && validStatuses.length > 0) {
        return this.areAllActionsInValidStatus(actionsOrNodes, validStatuses);
      }
      return true;
    }

    if (validStatuses && validStatuses.length > 0) {
      if (!this.areAllActionsInValidStatus(actionsOrNodes, validStatuses)) {
        return false;
      }
    }

    // For non-admin users, we need to check both assigned and unassigned actions
    const { assigned, unassigned } = this.categorizeSelectedActions(actionsOrNodes);
    
    if (assigned.length > 0) {
      const assignedNodes = actionsOrNodes.filter(node => 
        node.data && node.data.activeTeamId
      );
      
      if (!this.areAllSelectedTeamsInScope(assignedNodes, userTeams)) {
        return false;
      }
    }

    // Check unassigned actions - must be created by user if they have 'add client actions' permission
    if (unassigned.length > 0) {
      if (!this.canAddClientActions(vue)) {
        return false; 
      }
      
      if (!this.areAllUnassignedActionsCreatedByUser(unassigned)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Checks if user can assign selected actions
   * This is a wrapper around the generic method for backward compatibility
   * 
   * @param {Object} vue - Vue component instance
   * @param {Array} selectedNodes - Array of selected grid nodes
   * @returns {boolean}
   */
  static canAssignSelectedActions(vue, selectedNodes = []) {
    // Get assign options from the component (should be available from ClientActionAssign.getFormattedOptions())
    const assignOptions = vue.assignOptions || [];
    
    return this.canPerformActionOperation(
      vue, 
      vue.$perm.ACTIONS_ASSIGN, 
      selectedNodes, 
      assignOptions
    );
  }

  /**
   * Checks if user can submit "No Access" for selected actions
   * 
   * @param {Object} vue - Vue component instance
   * @param {Array} selectedNodes - Array of selected grid nodes
   * @returns {boolean}
   */
  static canSubmitNoAccess(vue, selectedNodes = []) {

    const assignOptions = vue.assignOptions || [];
    
    const validStatuses = [
      ActionStatus.ASSIGNED()
    ];
    
    return this.canPerformActionOperation(
      vue, 
      vue.$perm.ACTIONS_NO_ACCESS,
      selectedNodes, 
      assignOptions,
      validStatuses
    );
  }

  /**
   * Helper method specifically for dropdown visibility
   * Can be used in v-if directives for assign functionality
   * 
   * @param {Object} vue - Vue component instance
   * @param {Array} selectedNodes - Array of selected grid nodes (optional)
   * @returns {boolean}
   */
  static showAssignButton(vue, selectedNodes = []) {
    return this.canAssignSelectedActions(vue, selectedNodes);
  }

  /**
   * Helper method for No Access button visibility
   * Can be used in v-if directives for No Access functionality
   * 
   * @param {Object} vue - Vue component instance
   * @param {Array} selectedNodes - Array of selected grid nodes (optional)
   * @returns {boolean}
   */
  static showNoAccessButton(vue, selectedNodes = []) {
    return this.canSubmitNoAccess(vue, selectedNodes);
  }

  /**
   * Checks if user can respond to "No Access" for selected actions
   * Uses the same logic as assign operations - follows canPerformActionOperation pattern
   * 
   * @param {Object} vue - Vue component instance
   * @param {Array} selectedNodes - Array of selected grid nodes
   * @returns {boolean}
   */
  static canRespondToNoAccess(vue, selectedNodes = []) {

    const assignOptions = vue.assignOptions || [];
    

    const validStatuses = [ActionStatus.NO_ACCESS()];
    
    
    return this.canPerformActionOperation(
      vue, 
      vue.$perm.ACTIONS_NO_ACCESS, 
      selectedNodes, 
      assignOptions,
      validStatuses
    );
  }

  /**
   * Helper method for Respond to No Access button visibility
   * Can be used in v-if directives for Respond to No Access functionality
   * 
   * @param {Object} vue - Vue component instance
   * @param {Array} selectedNodes - Array of selected grid nodes (optional)
   * @returns {boolean}
   */
  static showRespondToNoAccessButton(vue, selectedNodes = []) {
    return this.canRespondToNoAccess(vue, selectedNodes);
  }

  /**
   * Debug helper to log permission state
   * Useful for troubleshooting permission issues
   * 
   * @param {Object} vue - Vue component instance
   * @param {Array} selectedNodes - Array of selected grid nodes
   */
  static debugPermissions(vue, selectedNodes = []) {
    const hasAssign = vue.$can(vue.$perm.ACTIONS_ASSIGN, vue.$perm.PERMISSION_TYPE_ACTION);
    const hasReject = vue.$can(vue.$perm.ACTIONS_REJECT, vue.$perm.PERMISSION_TYPE_ACTION);
    const hasAddClientActions = this.canAddClientActions(vue);
    const isAdminUser = this.isAdmin(vue);
    const teamIds = this.getSelectedTeamIds(selectedNodes);
    const assignOptions = vue.assignOptions || [];
    const allTeamsInScope = this.areAllSelectedTeamsInScope(selectedNodes, assignOptions);
    const { assigned, unassigned } = this.categorizeSelectedActions(selectedNodes);
    const allUnassignedCreatedByUser = this.areAllUnassignedActionsCreatedByUser(unassigned);
    const authUser = store.getters.authUser;
    const currentUserId = authUser && authUser.userId;

    
    const noAccessValidStatuses = [
      ActionStatus.ASSIGNED(),
      ActionStatus.PARTIAL(),
      ActionStatus.REJECTED(),
      ActionStatus.NO_ACCESS_RESPONSE(),
      ActionStatus.QUERY_RESPONSE()
    ];
    const allActionsValidForNoAccess = this.areAllActionsInValidStatus(selectedNodes, noAccessValidStatuses);

    console.group('🔍 Action Permission Debug');
    console.log('Has Assign Permission:', hasAssign);
    console.log('Has Reject Permission (No Access):', hasReject);
    console.log('Has Add Client Actions Permission:', hasAddClientActions);
    console.log('Is Admin:', isAdminUser);
    console.log('Current User ID:', currentUserId);
    console.log('Selected Team IDs:', teamIds);
    console.log('Available Assign Options:', assignOptions);
    console.log('All Teams In Scope:', allTeamsInScope);
    console.log('Assigned Actions:', assigned.length);
    console.log('Unassigned Actions:', unassigned.length);
    console.log('Unassigned Actions Created By User:', allUnassignedCreatedByUser);
    console.log('All Actions Valid for No Access:', allActionsValidForNoAccess);
    console.log('Action Statuses:', selectedNodes.map(node => ({
      id: node.data && node.data.clientActionId,
      status: node.data && node.data.status,
      createdByUserId: node.data && node.data.createdByUserId,
      isCreatedByCurrentUser: this.isActionCreatedByUser(node.data)
    })));
    console.log('Can Assign:', this.canAssignSelectedActions(vue, selectedNodes));
    console.log('Can Submit No Access:', this.canSubmitNoAccess(vue, selectedNodes));
    console.groupEnd();
  }
}

export default ActionPermissions;